import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, MessageSquare, Radar, UserCheck, Zap } from 'lucide-react';
import { ErrorState, LoadingState, describeError } from '@/components/primitives';
import { relTime } from '@/pages/inbox/inboxUtils';
import { formatUtcShort } from './threatUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const ICON = { reported: Radar, matched: Zap, status: CheckCircle2, owner: UserCheck, note: MessageSquare };
const NOTE_MAX = 2000;

/**
 * What happened to this brief, newest first: built by the server from recorded
 * events only (the report, the match, status and owner changes, notes).
 * Notes are kept with the brief for the whole team and appear in the audit log.
 */
export default function ActivityTab({ attackId, onCount, refreshKey }) {
  const [state, setState] = useState({ loading: true, error: null, events: [] });
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await axios.get(`${API}/inbox/${encodeURIComponent(attackId)}/activity`, { headers: headers() });
      setState({ loading: false, error: null, events: res.data?.events || [] });
      onCount?.(res.data?.events?.length || 0);
    } catch (error) {
      setState({ loading: false, error, events: [] });
    }
  }, [attackId, onCount]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const save = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setSaving(true);
    try {
      await axios.post(`${API}/inbox/${encodeURIComponent(attackId)}/notes`, { text }, { headers: headers() });
      setDraft('');
      toast.success('Note added');
      load();
    } catch (err) {
      toast.error("Couldn't add the note", { description: describeError(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="th-activity">
      <form className="th-composer" onSubmit={save}>
        <label htmlFor={`note-${attackId}`} className="th-label">Add a note for your team</label>
        <textarea
          id={`note-${attackId}`}
          value={draft}
          maxLength={NOTE_MAX}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') save(e); }}
          placeholder="What you checked, what you found, what happens next"
          rows={3}
        />
        <div className="th-composer-foot">
          <span className="th-muted">{draft.length > NOTE_MAX - 200 ? `${NOTE_MAX - draft.length} characters left` : 'Ctrl+Enter to add'}</span>
          <button type="submit" className="is-btn is-btn--primary is-btn--sm" disabled={saving || !draft.trim()}>{saving ? 'Adding…' : 'Add note'}</button>
        </div>
      </form>

      {state.loading && !state.events.length ? <LoadingState label="activity" rows={3} />
        : state.error ? <ErrorState variant="compact" title="Couldn't load the activity" error={state.error} onRetry={load} />
          : (
            <ol className="th-timeline" aria-label="Activity, newest first">
              {state.events.map((ev, i) => {
                const Icon = ICON[ev.kind] || CheckCircle2;
                return (
                  <li key={ev.id || `${ev.at}-${i}`}>
                    <span className="th-tl-dot" aria-hidden="true"><Icon /></span>
                    <div className="th-tl-body">
                      <div>
                        <span className="th-tl-who">{ev.actor || 'IntelliSecure'}</span>{' '}
                        <time className="th-muted" dateTime={ev.at} title={formatUtcShort(ev.at)}>{relTime(ev.at) === 'now' ? 'just now' : `${relTime(ev.at)} ago`}</time>
                      </div>
                      <div className="th-tl-text">{ev.text}</div>
                      {ev.note && <p className="th-tl-note">{ev.note}</p>}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
    </div>
  );
}
